/**
 * normalizeMath — Resilient LaTeX/markdown pre-processor for chat messages.
 *
 * The LLM is instructed to use \(...\) for inline and \[...\] for block math.
 * This function converts those to `$...$` / `$$...$$` which remark-math
 * reliably understands. Safety-net passes handle edge cases where the LLM
 * falls back to other styles.
 *
 * Pipeline:
 *   PASS 1  — \[...\]  → $$...$$  (block math)
 *   PASS 2  — \(...\)  → $...$   (inline math)
 *   PASS 3  — (\frac{a}{b}) → $\frac{a}{b}$ (parens wrapping LaTeX — safety net)
 *   PASS 4  — Chemistry equation detection (safety net)
 *   PASS 5  — Bare \command wrapping (safety net)
 *   PASS 6  — Adjacent $...$$ merge (safety net)
 *   PASS 7  — Geometry vertex + degree merges (safety net)
 *
 * Domain coverage (allowlist of ~80 commands):
 *   Math, chemistry, physics, Greek, sets/logic, geometry, calculus, misc.
 *
 * Idempotent — running on already-normalised text produces the same output.
 * Safe on non-math content (Hindi/Tamil/plain prose passes through untouched).
 */

// Known LaTeX commands across math + chemistry + physics + Greek + sets.
const LATEX_COMMANDS = [
  // Math
  'frac', 'sqrt', 'sum', 'int', 'prod', 'lim', 'log', 'ln', 'sin', 'cos', 'tan',
  'cot', 'sec', 'csc', 'arcsin', 'arccos', 'arctan', 'sinh', 'cosh', 'tanh',
  // Operators
  'times', 'cdot', 'div', 'pm', 'mp', 'neq', 'approx', 'equiv', 'leq', 'geq',
  'll', 'gg', 'sim',
  // Arrows / chemistry
  'to', 'rightarrow', 'leftarrow', 'Rightarrow', 'Leftarrow', 'Leftrightarrow',
  'rightleftharpoons', 'mapsto', 'iff', 'implies', 'longrightarrow',
  // Greek lowercase
  'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'varepsilon', 'zeta', 'eta',
  'theta', 'vartheta', 'iota', 'kappa', 'lambda', 'mu', 'nu', 'xi', 'pi',
  'varpi', 'rho', 'varrho', 'sigma', 'varsigma', 'tau', 'upsilon', 'phi',
  'varphi', 'chi', 'psi', 'omega',
  // Greek uppercase
  'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta', 'Theta',
  'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Pi', 'Rho', 'Sigma', 'Tau',
  'Upsilon', 'Phi', 'Chi', 'Psi', 'Omega',
  // Sets/logic
  'in', 'notin', 'subset', 'subseteq', 'supset', 'supseteq', 'cup', 'cap',
  'emptyset', 'forall', 'exists', 'neg', 'lor', 'land',
  // Geometry
  'angle', 'triangle', 'square', 'parallel', 'perp', 'circ', 'deg',
  // Calculus / vector
  'vec', 'hat', 'bar', 'overline', 'underline', 'mathrm', 'mathbf', 'mathit',
  'text', 'nabla', 'partial', 'prime',
  // Misc
  'ldots', 'cdots', 'dots', 'vdots', 'ddots', 'infty', 'aleph',
];

// Regex to confirm content contains a known LaTeX command.
// (?![a-z]) blocks partial matches (\sim in \simple) but allows \angleQPR.
const KNOWN_LATEX_RE = new RegExp(
  '\\\\(?:' + LATEX_COMMANDS.join('|') + ')(?![a-z])',
);

export function normalizeMath(rawText) {
  if (!rawText || typeof rawText !== 'string') return rawText;
  let t = rawText;

  // -------------------------------------------------------------------------
  // PASS 0 — Safety net: Regular parens wrapping LaTeX: `(\frac{a}{b})`
  // Runs BEFORE \[...\] and \(...\) conversion so it doesn't operate inside
  // already-delimited math blocks. Lookbehind skips function arguments like
  // \cos(\angle PQR) where parens are part of the expression, not wrappers.
  // -------------------------------------------------------------------------
  t = t.replace(/(?<![a-zA-Z\\])\(([^()\n]*?)\)/g, (whole, content) => {
    if (content.includes('$')) return whole;
    if (KNOWN_LATEX_RE.test(content)) {
      return `$${content}$`;
    }
    return whole;
  });

  // -------------------------------------------------------------------------
  // PASS 1 — LaTeX-native block delimiters: `\[...\]` → `$$...$$`
  // This is the PRIMARY conversion path (model is instructed to use \[...\]).
  // -------------------------------------------------------------------------
  t = t.replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (_, m) => `\n\n$$${m}$$\n\n`);

  // -------------------------------------------------------------------------
  // PASS 1.5 — Safety net: Inline $$...$$ → $...$
  // If the model falls back to $$...$$ mid-sentence, downgrade to $.
  // True block math ($$...$$ on its own line) is unaffected.
  // -------------------------------------------------------------------------
  t = t.replace(/([^\n])\$\$([^$\n]+?)\$\$([^\n])/g, (_, before, content, after) => {
    return `${before}$${content}$${after}`;
  });

  // -------------------------------------------------------------------------
  // PASS 2 — LaTeX-native inline delimiters: `\(...\)` → `$...$`
  // This is the PRIMARY conversion path (model is instructed to use \(...\)).
  // -------------------------------------------------------------------------
  t = t.replace(/\\\(\s*([\s\S]*?)\s*\\\)/g, (_, m) => `$${m}$`);

  // -------------------------------------------------------------------------
  // PASS 4 — Safety net: Chemistry equation detection (subscript + arrow).
  // -------------------------------------------------------------------------
  t = t.split('\n').map((line) => {
    if (line.includes('$')) return line;
    const hasSubscript = /[A-Za-z]_\d|[A-Za-z]_\{[^{}]+\}/.test(line);
    const hasChemArrow = /\\(?:rightleftharpoons|rightarrow|leftarrow|to|longrightarrow)\b/.test(line);
    if (hasSubscript && hasChemArrow) {
      return `\n$$${line.trim()}$$\n`;
    }
    return line;
  }).join('\n');

  // -------------------------------------------------------------------------
  // PASS 5 — Safety net: Bare LaTeX commands NOT inside any `$...$`.
  // Wraps each isolated `\command{...}` in `$...$`.
  // -------------------------------------------------------------------------
  t = walkAndWrap(t);

  // Tidy any accidental double-wraps from cumulative passes.
  t = t.replace(/\$\$\$+/g, '$$$$').replace(/\${3,}/g, '$$');

  // -------------------------------------------------------------------------
  // PASS 6 — Safety net: Merge adjacent inline-math blocks.
  // $A$$B$ → $A B$ (prevents remark-math $$ confusion).
  // -------------------------------------------------------------------------
  let prev;
  do {
    prev = t;
    t = t.replace(/\$([^$\n]+?)\$\$([^$\n]+?)\$/g, (_, a, b) => `$${a} ${b}$`);
  } while (t !== prev);

  // -------------------------------------------------------------------------
  // PASS 7 — Safety net: Targeted structural merges.
  // -------------------------------------------------------------------------
  // 7A: $\angle$ QPR → $\angle QPR$
  t = t.replace(/\$(\\(?:angle|triangle))\$\s?([A-Z]{2,5})/g, (_, cmd, vars) => `$${cmd} ${vars}$`);
  // 7B: 45^$\circ$ → $45^\circ$
  t = t.replace(/(\d+)\^\$(\\circ)\$/g, (_, num, cmd) => `$${num}^${cmd}$`);
  // 7C: 180$^\circ$ → $180^\circ$
  t = t.replace(/(\d+)\$\^(\\circ)\$/g, (_, num, cmd) => `$${num}^${cmd}$`);

  return t;
}


/**
 * Walk the text, leaving `$...$` regions untouched, and wrap any bare LaTeX
 * command (matching the allowlist) + its 0-2 brace-arg groups in `$...$`.
 */
function walkAndWrap(text) {
  const tokenRe = new RegExp(
    [
      // Group 1: protected math: $$...$$ or $...$
      String.raw`(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)`,
      // Group 2: known LaTeX command ((?![a-z]) allows uppercase variable names)
      String.raw`(\\(?:${LATEX_COMMANDS.join('|')})(?![a-z])(?:\{(?:[^{}]|\{[^{}]*\})*\}){0,2})`,
    ].join('|'),
    'g',
  );

  return text.replace(tokenRe, (full, protectedSegment, command) => {
    if (protectedSegment) return protectedSegment;
    return `$${command}$`;
  });
}

export default normalizeMath;
